import { LineItem, RevenuePeriod } from '@vendasta/sales-orders';
import { Injectable } from '@angular/core';
import { Frequency } from '@vendasta/galaxy/frequency';
import { TaxOptions } from '@vendasta/sales-orders/lib/_internal';
import { getTaxAmountOfLineItemForRevenuePeriodPrice } from '../edit-order/revenue-utils';

import {
  convertFuturePaymentItemsMapIntoFuturePaymentDisplayItemArray,
  getLineItemsDueToDate,
} from '../../shared/utils';
import {
  isLineItemRecurring,
  convertEndDateToDuration,
  FuturePaymentScheduledTime,
  FuturePaymentItemsMapWithCurrency,
  ItemRowMapByDateAndDuration,
} from '@galaxy/inventory-ui';
import { FuturePaymentDisplayItemsWithCurrency } from '../future-payments-display/future-payments-display.component';
const RevenuePeriodToFrequency = {
  [RevenuePeriod.ONETIME]: Frequency.ONE_TIME,
  [RevenuePeriod.MONTHLY]: Frequency.MONTHLY,
  [RevenuePeriod.YEARLY]: Frequency.YEARLY,
};

@Injectable()
export class RetailSummaryService {
  public constructFuturePaymentLineItemsArray(
    lineItems: LineItem[],
    taxOptions: TaxOptions[],
    contractStartDate: Date,
  ): FuturePaymentDisplayItemsWithCurrency {
    if (!lineItems.length) return {} as FuturePaymentDisplayItemsWithCurrency;
    const futurePaymentItemsMap = this.calculateLineItemsFuturePayments(lineItems, taxOptions, contractStartDate);
    return convertFuturePaymentItemsMapIntoFuturePaymentDisplayItemArray(
      futurePaymentItemsMap?.lineItemsMap,
      futurePaymentItemsMap?.currencyCode,
    );
  }

  public calculateLineItemsFuturePayments(
    lineItems: LineItem[],
    taxOptions: TaxOptions[],
    contractStartDate: Date,
  ): FuturePaymentItemsMapWithCurrency {
    if (!lineItems?.length) return {} as FuturePaymentItemsMapWithCurrency;

    const dueNowItems = getLineItemsDueToDate(lineItems, contractStartDate);

    const currencyCode = lineItems[0].currencyCode;

    const futurePaymentsGrouped: ItemRowMapByDateAndDuration = {};

    lineItems.forEach((lineItem) => {
      const startDate = lineItem.billingPeriod?.startDate ? new Date(lineItem.billingPeriod?.startDate) : undefined;
      const endDate = lineItem.billingPeriod?.endDate ? new Date(lineItem.billingPeriod?.endDate) : undefined;

      const duration = lineItem.billingPeriod?.duration?.value;

      const revenueComponents = lineItem.currentRevenue?.revenueComponents;

      let numericStartDate = startDate?.getTime();

      //If items is due now, set to StartChargingImmediately
      if (!numericStartDate || dueNowItems.includes(lineItem)) {
        numericStartDate = FuturePaymentScheduledTime.StartChargingImmediately;
      }

      if (!futurePaymentsGrouped[numericStartDate]) {
        futurePaymentsGrouped[numericStartDate] = {};
      }

      revenueComponents.forEach((revenueComponent) => {
        const frequency = RevenuePeriodToFrequency[revenueComponent.period] || Frequency.ONE_TIME; // one time products and setup fees can return undefined period
        let componentDuration = duration;

        if (!componentDuration && endDate) {
          componentDuration = convertEndDateToDuration(startDate || new Date(), endDate, frequency);
        }

        if (frequency === Frequency.ONE_TIME) {
          componentDuration = FuturePaymentScheduledTime.NoRenewal;
        } else if (!componentDuration) {
          componentDuration = FuturePaymentScheduledTime.RenewIndefinitely;
        }

        const price = this.calculateLineItemPrice(
          revenueComponent.value,
          lineItem.quantity,
          lineItem.taxRateIds,
          taxOptions,
        );

        //Skip items that are not recurring or are free
        if (!isLineItemRecurring(startDate, contractStartDate, componentDuration, frequency) || !price) {
          return;
        }

        //Initialize duration dictionary if it doesnt exist
        if (!futurePaymentsGrouped[numericStartDate][componentDuration]) {
          futurePaymentsGrouped[numericStartDate][componentDuration] = {};
        }

        //initialize price if it doesnt exist, add to it otherwise
        if (!futurePaymentsGrouped[numericStartDate][componentDuration][frequency]) {
          futurePaymentsGrouped[numericStartDate][componentDuration][frequency] = price;
        } else {
          futurePaymentsGrouped[numericStartDate][componentDuration][frequency] += price;
        }
      });
    });

    return { lineItemsMap: futurePaymentsGrouped, currencyCode: currencyCode };
  }

  private calculateLineItemPrice(
    price: number,
    quantity: number,
    taxRateIds: string[],
    taxOptions: TaxOptions[],
  ): number {
    const tax = getTaxAmountOfLineItemForRevenuePeriodPrice(price, quantity, taxRateIds, taxOptions);
    if (!price || !quantity) return 0;
    return price * quantity + tax;
  }
}
